import { createGlobalStyle } from 'styled-components'

const GlobalStyles = createGlobalStyle`
  /* Reset CSS premium pour partir sur de bonnes bases */
  * {
    box-sizing: border-box;
    margin: 0;
    padding: 0;
  }

  /* Configuration de base pour un rendu luxueux */
  html {
    font-size: 16px;
    scroll-behavior: smooth;
    /* Hauteur 100% pour permettre aux enfants d'utiliser toute la hauteur */
    height: 100%;
    /* Amélioration du rendu des polices pour plus de netteté */
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;
    text-rendering: optimizeLegibility;
  }

  body {
    font-family: ${props => props.theme.fonts.secondary};
    color: ${props => props.theme.colors.textPrimary};
    background-color: ${props => props.theme.colors.background};
    line-height: 1.7;
    letter-spacing: 0.01em;
    font-weight: 400;
    /* Hauteur 100% pour éviter les espaces blancs en bas */
    height: 100%;
    margin: 0;
    padding: 0;

    /* Curseur par défaut élégant */
    cursor: default;

    /* Style de sélection personnalisé */
    ::selection {
      background: ${props => props.theme.colors.secondary}40;
      color: ${props => props.theme.colors.primary};
    }
  }

  /* S'assurer que #root prend toute la hauteur */
  #root {
    min-height: 100%;
  }

  /* Typographie luxueuse pour les titres */
  h1, h2, h3, h4, h5, h6 {
    font-family: ${props => props.theme.fonts.primary};
    font-weight: ${props => props.theme.fontWeights.light};
    line-height: 1.3;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 1.5rem;
    letter-spacing: -0.02em; /* Crénage négatif pour l'élégance */
    
    /* Transition subtile pour les effets au survol */
    transition: ${props => props.theme.transitions.luxury};
  }

  /* Tailles spécifiques pour chaque niveau de titre */
  h1 {
    font-size: ${props => props.theme.fontSizes.giant};
    font-weight: ${props => props.theme.fontWeights.extraBold};
    letter-spacing: -0.03em;
  }

  h2 {
    font-size: ${props => props.theme.fontSizes.massive};
    font-weight: ${props => props.theme.fontWeights.light};
  }

  h3 {
    font-size: ${props => props.theme.fontSizes.xxxl};
    font-weight: ${props => props.theme.fontWeights.medium};
  }

  h4 {
    font-size: ${props => props.theme.fontSizes.xxl};
    font-weight: ${props => props.theme.fontWeights.medium};
  }

  h5 {
    font-size: ${props => props.theme.fontSizes.xl};
    font-weight: ${props => props.theme.fontWeights.semiBold};
  }

  h6 {
    font-size: ${props => props.theme.fontSizes.lg};
    font-weight: ${props => props.theme.fontWeights.semiBold};
  }

  /* Paragraphes avec styles raffinés */
  p {
    margin-bottom: 1.5rem;
    color: ${props => props.theme.colors.textSecondary};
    line-height: 1.8;
    font-weight: 400;
    
    /* Classe pour les paragraphes d'introduction */
    &.lead {
      font-size: ${props => props.theme.fontSizes.xl};
      font-weight: 300;
      color: ${props => props.theme.colors.textPrimary};
      letter-spacing: 0.02em;
      line-height: 1.9;
    }
    
    /* Classe pour les petits textes */
    &.small {
      font-size: ${props => props.theme.fontSizes.sm};
      opacity: 0.9;
    }
  }

  /* Liens avec effet premium au survol */
  a {
    color: ${props => props.theme.colors.secondary};
    text-decoration: none;
    transition: ${props => props.theme.transitions.luxury};
    position: relative;
    
    &:hover {
      color: ${props => props.theme.colors.primary};
      
      /* Soulignement animé au survol */
      &::after {
        content: '';
        position: absolute;
        bottom: -2px;
        left: 0;
        right: 0;
        height: 1px;
        background: ${props => props.theme.colors.primary};
        animation: underline 0.3s ease forwards;
      }
    }

    &:focus {
      outline: 2px solid ${props => props.theme.colors.secondary};
      outline-offset: 3px;
      border-radius: 2px;
    }
  }

  /* Animation de soulignement pour les liens */
  @keyframes underline {
    from { transform: scaleX(0); }
    to { transform: scaleX(1); }
  }

  /* Éléments de formulaire avec style luxueux */
  input, select, textarea, button {
    font-family: inherit;
    font-size: inherit;
    border-radius: ${props => props.theme.borderRadius.md};
    transition: ${props => props.theme.transitions.luxury};
    font-weight: 400;
  }

  /* Champs de formulaire avec bordures élégantes */
  input, select, textarea {
    border: 1px solid rgba(196, 164, 124, 0.2);
    padding: 1rem 1.25rem;
    background: white;
    color: ${props => props.theme.colors.textPrimary};
    
    &:focus {
      outline: none;
      border-color: ${props => props.theme.colors.secondary};
      box-shadow: 0 0 0 3px ${props => props.theme.colors.secondary}25;
      background: ${props => props.theme.colors.backgroundLight};
    }

    &:disabled {
      background-color: ${props => props.theme.colors.backgroundLight};
      cursor: not-allowed;
      opacity: 0.7;
      border-color: rgba(196, 164, 124, 0.1);
    }
    
    /* Placeholder élégant et discret */
    &::placeholder {
      color: ${props => props.theme.colors.textMuted};
      opacity: 0.8;
      font-weight: 300;
    }
  }

  /* Style par défaut des boutons (si pas de classe custom) */
  button {
    cursor: pointer;
    border: none;
    font-weight: ${props => props.theme.fontWeights.medium};
    letter-spacing: 0.025em;
    position: relative;
    overflow: hidden;
    
    /* Style premium si pas de classe styled-components */
    &:not([class*="styled"]) {
      padding: 0.75rem 2rem;
      background: ${props => props.theme.colors.primary};
      color: white;
      border-radius: ${props => props.theme.borderRadius.md};
      transition: ${props => props.theme.transitions.luxury};
      
      &:hover:not(:disabled) {
        background: ${props => props.theme.colors.primaryDark};
        transform: translateY(-2px);
        box-shadow: ${props => props.theme.shadows.luxury};
      }
    }

    &:focus {
      outline: 2px solid ${props => props.theme.colors.secondary};
      outline-offset: 2px;
    }

    &:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      transform: none !important;
    }
  }

  /* Listes avec style élégant */
  ul, ol {
    padding-left: 1.75rem;
    margin-bottom: 1.5rem;
  }

  li {
    margin-bottom: 0.75rem;
    line-height: 1.7;
    color: ${props => props.theme.colors.textSecondary};
  }

  /* Images responsives avec effet au survol */
  img {
    max-width: 100%;
    height: auto;
    border-radius: ${props => props.theme.borderRadius.md};
    transition: ${props => props.theme.transitions.luxury};
    
    /* Zoom subtil au survol */
    &:hover {
      transform: scale(1.02);
      box-shadow: ${props => props.theme.shadows.lg};
    }
  }

  /* Tables élégantes pour afficher des données */
  table {
    width: 100%;
    border-collapse: collapse;
    margin-bottom: 2rem;
    border: 1px solid rgba(196, 164, 124, 0.15);
    border-radius: ${props => props.theme.borderRadius.lg};
    overflow: hidden;
    box-shadow: ${props => props.theme.shadows.sm};
  }

  th, td {
    padding: 1rem 1.25rem;
    text-align: left;
    border-bottom: 1px solid rgba(196, 164, 124, 0.1);
    vertical-align: top;
  }

  th {
    font-weight: ${props => props.theme.fontWeights.semiBold};
    background: ${props => props.theme.colors.backgroundLight};
    color: ${props => props.theme.colors.primary};
    font-size: ${props => props.theme.fontSizes.sm};
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  td {
    font-size: ${props => props.theme.fontSizes.md};
    color: ${props => props.theme.colors.textSecondary};
  }

  /* Classes utilitaires pour l'alignement du texte */
  .text-center { text-align: center; }
  .text-right { text-align: right; }
  .text-left { text-align: left; }
  
  /* Classes utilitaires pour les couleurs de texte */
  .text-primary { color: ${props => props.theme.colors.primary}; }
  .text-secondary { color: ${props => props.theme.colors.secondary}; }
  .text-success { color: ${props => props.theme.colors.success}; }
  .text-warning { color: ${props => props.theme.colors.warning}; }
  .text-error { color: ${props => props.theme.colors.error}; }
  .text-muted { color: ${props => props.theme.colors.textMuted}; }

  /* Classes utilitaires pour les marges bottom */
  .mb-0 { margin-bottom: 0; }
  .mb-1 { margin-bottom: 0.5rem; }
  .mb-2 { margin-bottom: 1rem; }
  .mb-3 { margin-bottom: 1.5rem; }
  .mb-4 { margin-bottom: 2rem; }
  .mb-5 { margin-bottom: 3rem; }

  /* Classes utilitaires pour les marges top */
  .mt-0 { margin-top: 0; }
  .mt-1 { margin-top: 0.5rem; }
  .mt-2 { margin-top: 1rem; }
  .mt-3 { margin-top: 1.5rem; }
  .mt-4 { margin-top: 2rem; }
  .mt-5 { margin-top: 3rem; }

  /* Animations premium réutilisables */
  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  @keyframes slideInLeft {
    from {
      opacity: 0;
      transform: translateX(-40px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(40px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }

  /* Classes d'animation prêtes à l'emploi */
  .fade-in-up {
    animation: fadeInUp 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  }

  .fade-in-down {
    animation: fadeInDown 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  }

  .slide-in-left {
    animation: slideInLeft 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  }

  .slide-in-right {
    animation: slideInRight 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  }

  .scale-in {
    animation: scaleIn 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
  }

  /* Scrollbar personnalisée pour plus d'élégance */
  ::-webkit-scrollbar {
    width: 8px;
  }

  ::-webkit-scrollbar-track {
    background: ${props => props.theme.colors.backgroundLight};
  }

  ::-webkit-scrollbar-thumb {
    background: ${props => props.theme.colors.secondary};
    border-radius: 4px;
    transition: background 0.3s ease;
  }

  ::-webkit-scrollbar-thumb:hover {
    background: ${props => props.theme.colors.primary};
  }

  /* Ajustements responsive pour mobile */
  @media (max-width: ${props => props.theme.breakpoints.lg}) {
    html {
      font-size: 15px; /* Réduction subtile de la taille de base */
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    html {
      font-size: 14px;
    }
    
    body {
      line-height: 1.6; /* Interligne plus serré sur mobile */
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.sm}) {
    html {
      font-size: 13px;
    }
  }

  /* Amélioration de l'accessibilité avec focus visible */
  *:focus-visible {
    outline: 2px solid ${props => props.theme.colors.secondary};
    outline-offset: 2px;
    border-radius: 3px;
  }

  /* Amélioration de la lisibilité des textes */
  strong, b {
    font-weight: ${props => props.theme.fontWeights.semiBold};
    color: ${props => props.theme.colors.primary};
  }

  em, i {
    font-style: italic;
    color: ${props => props.theme.colors.secondary};
  }

  /* Gestion des espacements entre sections */
  section {
    position: relative;
  }

  section + section {
    margin-top: 0; /* Géré individuellement dans chaque composant */
  }

  /* Optimisations spécifiques pour mobile */
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    /* Réduction globale des espacements */
    section {
      padding: 3rem 0 !important; 
    }
    
    /* Ajustement des marges de titre */
    h2 {
      margin-bottom: 1rem;
    }
    
    /* Réduction des marges de paragraphe */
    p {
      margin-bottom: 1rem;
    }
    
    /* Ajustement de la hauteur du hero */
    .hero-section {
      min-height: calc(100vh - 60px);
    }
  }
`

export default GlobalStyles